import greedCautious from '@/assets/mitra-3d/greed-cautious.png'
import greedFear from '@/assets/mitra-3d/greed-fear.png'
import greedGreed from '@/assets/mitra-3d/greed-greed.png'
import greedNeutral from '@/assets/mitra-3d/greed-neutral.png'
import greedWarm from '@/assets/mitra-3d/greed-warm.png'

/**
 * The company greed meter's words, shared by Overview and the Stock rail so
 * the two never name one reading differently.
 *
 * mm-be sends a five-band verdict CODE and leaves the word to the client
 * ("Display labels/scale are the frontend's to map", services/company/
 * types.py `Sentiment`). These are the app's own five band names, the ones
 * `MoodGauge` draws its five bands for and the market overview buckets a
 * score into (fear · cautious · neutral · warm · greed), matched to mm-be's
 * bands by position. NABIL's 25 arrives as FEAR, the second band, and the
 * frame prints it "Cautious".
 *
 * The tone mirrors the band the marker sits in: the fear side wears the
 * track's red, the greed side its green — the same deliberate exception to
 * "market colour means direction" that `MoodGauge` documents for the track.
 */
const BANDS = {
  EXTREME_FEAR: { word: 'Fear', tone: 'down', art: greedFear },
  FEAR: { word: 'Cautious', tone: 'down', art: greedCautious },
  NEUTRAL: { word: 'Neutral', tone: 'neutral', art: greedNeutral },
  GREED: { word: 'Warm', tone: 'up', art: greedWarm },
  EXTREME_GREED: { word: 'Greed', tone: 'up', art: greedGreed },
} as const

export type SentimentBand = (typeof BANDS)[keyof typeof BANDS]

/** Left to right, the order `MoodGauge` draws its bands in. */
export const SENTIMENT_BAND_WORDS = Object.values(BANDS).map((band) => band.word)

const isBandCode = (code: string): code is keyof typeof BANDS => Object.hasOwn(BANDS, code)

/**
 * The band for mm-be's verdict. Null for a missing or unknown one (mm-be
 * documents NONE on bad data): it costs the word, never the reading.
 *
 * Read as a code whatever its spelling. The model documents EXTREME_FEAR …
 * EXTREME_GREED (apps/market/models.py `CompanyEmotion.verdict`), yet mm-be's
 * own dev bootstrap writes the word "Greed", and the legacy cron fills the
 * table on real servers.
 */
export const sentimentBand = (verdict: string | null): SentimentBand | null => {
  if (verdict === null) return null
  const code = verdict
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_')
  return isBandCode(code) ? BANDS[code] : null
}

/** Mitra in the band's pose (the mock's `mitra-greed-*` art); the neutral pose with no verdict. */
export const sentimentArt = (band: SentimentBand | null): string => band?.art ?? greedNeutral
